import React, { useEffect, useState } from 'react'
import { IoIosArrowDown, IoMdCheckmark } from 'react-icons/io'
import { IoClose } from 'react-icons/io5'
import Select, { SingleValue } from 'react-select'

import {
  incrementSelectStyles,
  SimpleOptionType,
} from '../../../../../customStyles/ReactSelectCustomStyle'
import { useOutsideClick } from '../../../../../hooks/useOutsideClick'
import { formatIncrementTime, ParsedTime, parseIncrementTime } from '../schedulingUtils'

import { HOURS_OPTIONS, MINUTES_OPTIONS, SECONDS_OPTIONS } from '../../../../../fakeData/TimeList'

interface IncrementTimeSelectorProps {
  initialTime?: string
  saveHandler: (time: string) => void
  placeholder?: string
}

const IncrementTimeSelector: React.FC<IncrementTimeSelectorProps> = ({
  initialTime,
  saveHandler,
  placeholder = 'Increment',
}) => {
  const [editMode, setEditMode] = useState(false)
  const [isEditingPending, setIsEditingPending] = useState(false)
  const ref = useOutsideClick(() => {
    setEditMode(false)
  })

  const [selectedTime, setSelectedTime] = useState<ParsedTime>(() =>
    initialTime ? parseIncrementTime(initialTime) : { hour: '00', minute: '00', second: '00' }
  )

  const handleOpenEditMode = () => {
    if (!initialTime) {
      setSelectedTime({ hour: '00', minute: '00', second: '00' })
      setIsEditingPending(true)
    } else {
      setSelectedTime(parseIncrementTime(initialTime))
      setEditMode(true)
    }
  }

  useEffect(() => {
    if (isEditingPending) {
      setEditMode(true)
      setIsEditingPending(false)
    }
  }, [selectedTime, isEditingPending])

  const handleTimeChange = (
    type: 'hour' | 'minute' | 'second',
    option: SingleValue<{ value: string; label: string }>
  ) => {
    if (option) {
      setSelectedTime((prev) => ({ ...prev, [type]: option.value }))
    }
  }

  const handleSave = () => {
    const formattedTime = formatIncrementTime(
      selectedTime.hour,
      selectedTime.minute,
      selectedTime.second
    )
    saveHandler(formattedTime)
    setEditMode(false)
  }

  if (!editMode) {
    return (
      <div className="h-[40px] flex justify-start items-center">
        <div
          className="cursor-pointer inline-flex justify-start items-center"
          onClick={(e) => {
            e.stopPropagation()
            handleOpenEditMode()
          }}
        >
          {initialTime ? (
            <div className="flex items-center gap-1 rounded-full text-sm text-SeabiscuitDark200ThemeColor">
              <span>
                {selectedTime.hour}:{selectedTime.minute}:{selectedTime.second}
              </span>
              <IoIosArrowDown className="w-4 h-4 text-SeabiscuitDark200ThemeColor/40 ml-1" />
            </div>
          ) : (
            <span className="text-nr flex gap-3 items-center text-SeabiscuitDark200ThemeColor/50 justify-center">
              {placeholder}
              <IoIosArrowDown className="w-4 h-4 text-SeabiscuitDark200ThemeColor/40" />
            </span>
          )}
        </div>
      </div>
    )
  }

  return (
    <div ref={ref} className="flex max-w-[120px] items-center">
      <div onClick={(e) => e.stopPropagation()} className="flex w-full">
        <Select
          classNamePrefix="select"
          options={HOURS_OPTIONS}
          value={HOURS_OPTIONS.find((h) => h.value === selectedTime.hour)}
          onChange={(opt) => handleTimeChange('hour', opt as SimpleOptionType)}
          styles={incrementSelectStyles}
          className="flex-1 px-[0!important] h-[40px] bg-[#fff] flex items-center outline-0  w-full focus:ring-0 focus:outline-0 no-underline rounded-l-[4px]"
          isClearable={false}
          components={{ DropdownIndicator: null }}
          isSearchable={true}
        />
      </div>

      <div onClick={(e) => e.stopPropagation()} className="flex w-full">
        <Select
          classNamePrefix="select"
          options={MINUTES_OPTIONS}
          value={MINUTES_OPTIONS.find((m) => m.value === selectedTime.minute)}
          onChange={(opt) => handleTimeChange('minute', opt as SimpleOptionType)}
          styles={incrementSelectStyles}
          className="flex-1 px-[0!important] h-[40px] border-none flex items-center outline-0  w-full focus:ring-0 focus:border-0 focus:outline-0 bg-[#fff] !rounded-none"
          isClearable={false}
          components={{ DropdownIndicator: null }}
          isSearchable={true}
        />
      </div>

      <div onClick={(e) => e.stopPropagation()} className="flex w-full">
        <Select
          classNamePrefix="select"
          options={SECONDS_OPTIONS}
          value={SECONDS_OPTIONS.find((s) => s.value === selectedTime.second)}
          onChange={(opt) => handleTimeChange('second', opt as SimpleOptionType)}
          styles={incrementSelectStyles}
          components={{ DropdownIndicator: null }}
          className="flex-1 h-[40px] border-none px-[0!important] flex items-center outline-0  w-full focus:ring-0 focus:border-0 focus:outline-0 bg-[#fff] rounded-r-[4px]"
          isClearable={false}
          isSearchable
        />
      </div>

      <div className="flex items-center gap-2 ml-1">
        <button onClick={() => setEditMode(false)}>
          <IoClose className="h-4 w-4 text-SeabiscuitDark200ThemeColor hover:text-SeabiscuitDark200ThemeColor/80" />
        </button>
        <button onClick={handleSave}>
          <IoMdCheckmark className="h-4 w-4 text-SeabiscuitDark200ThemeColor hover:text-SeabiscuitDark200ThemeColor/80" />
        </button>
      </div>
    </div>
  )
}

export default IncrementTimeSelector
